import { useMutation } from '@tanstack/react-query';

import { type MemberDetail, membersRepository } from '@web/data/repositories';
import { t } from '@web/i18n';
import { useAuthz, useSnackbar } from '@web/ui/core';

interface UseMcpAccessParams {
  orgId: string;
  members: MemberDetail[];
  invalidateList: () => Promise<void>;
  onError: (err: unknown) => void;
}

interface UseMcpAccessResult {
  /** Mirrors the backend `accounts:members:features-write` permission. */
  canGrantMcp: boolean;
  setMcpAccess: (userId: string, enabled: boolean) => void;
}

/**
 * Grants/revokes the per-member `mcp` feature. Split out of `useMembersPage`
 * to keep that hook under the max-lines-per-function cap — this is the only
 * consumer today.
 */
export function useMcpAccess({
  orgId,
  members,
  invalidateList,
  onError,
}: UseMcpAccessParams): UseMcpAccessResult {
  const authz = useAuthz();
  const { showSnackbar } = useSnackbar();

  const featuresMutation = useMutation({
    mutationFn: (input: { userId: string; features: string[] }) =>
      membersRepository.setFeatures(orgId, input.userId, input.features),
    onSuccess: async (_data, input) => {
      await invalidateList();
      showSnackbar({
        message: input.features.includes('mcp')
          ? t('members.toast.mcpOn')
          : t('members.toast.mcpOff'),
        severity: 'success',
      });
    },
    onError,
  });

  return {
    canGrantMcp: authz.has('accounts:members:features-write'),
    setMcpAccess: (userId, enabled) => {
      // Raw feature strings travel end to end: the server replaces the whole set,
      // so a feature this bundle does not know must be sent back untouched.
      const current = members.find((m) => m.userId === userId)?.features ?? [];
      const next = enabled ? [...new Set([...current, 'mcp'])] : current.filter((f) => f !== 'mcp');
      featuresMutation.mutate({ userId, features: next });
    },
  };
}
